import React from 'react';

const MultiSelect = ({
  options = [],
  selectedValues = [],
  value, // For controlled component (react-hook-form)
  onChange, // For onChange pattern (react-hook-form)
  onAdd, // For onAdd/onRemove pattern (existing)
  onRemove, // For onAdd/onRemove pattern (existing)
  formMethods, // react-hook-form methods
  fieldName, // Field name for react-hook-form
  placeholder = "Select an option...",
  disabled = false,
  className = "",
  selectClassName = "form-input",
  containerClassName = "",
  chipClassName = "",
  requiredValue = null, // Value that cannot be removed
  allSelectedValue = null, // Special "All" option
  allSelected = false,
  onToggleAll = null,
  renderChip = null, // Custom chip renderer
  renderAllChip = null, // Custom "All" chip renderer
  ...props
}) => {
  // Determine the current selected values
  // Priority: value prop > formMethods.getValues > selectedValues prop
  const getCurrentValues = () => {
    if (value !== undefined) {
      return Array.isArray(value) ? value : [];
    }
    if (formMethods && fieldName) {
      const formValue = formMethods.getValues(fieldName);
      return Array.isArray(formValue) ? formValue : [];
    }
    return selectedValues || [];
  };

  const currentValues = getCurrentValues();

  const availableOptions = options.filter(
    (option) => !currentValues.includes(option.value),
  );

  // Helper function to get label for a value
  const getLabelForValue = (value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value; // Fallback to value if not found
  };

  // Handle adding a value
  const handleAddValue = (newValue) => {
    if (!newValue || currentValues.includes(newValue)) {
      return;
    }

    const updatedValues = [...currentValues, newValue];

    // Use onChange pattern (react-hook-form)
    if (onChange) {
      if (formMethods && fieldName) {
        formMethods.setValue(fieldName, updatedValues, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        onChange(updatedValues);
      }
    }
    // Use onAdd pattern (existing)
    else if (onAdd) {
      onAdd(newValue);
    }
  };

  // Handle removing a value
  const handleRemoveValue = (valueToRemove) => {
    if (valueToRemove === requiredValue) {
      return;
    }

    const updatedValues = currentValues.filter(v => v !== valueToRemove);

    // Use onChange pattern (react-hook-form)
    if (onChange) {
      if (formMethods && fieldName) {
        formMethods.setValue(fieldName, updatedValues, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        onChange(updatedValues);
      }
    }
    // Use onRemove pattern (existing)
    else if (onRemove) {
      onRemove(valueToRemove);
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      handleAddValue(selectedValue);
      e.target.value = ''; // Reset select
    }
  };

  const handleRemove = (value) => {
    handleRemoveValue(value);
  };

  const handleToggleAll = () => {
    if (onToggleAll) {
      onToggleAll();
    }
  };

  const defaultRenderChip = (value, index) => {
    const displayLabel = getLabelForValue(value);
    return (
      <div key={value} className={`selected-chip ${chipClassName}`}>
        <span className="chip-text">{displayLabel}</span>
        {!disabled && value !== requiredValue && (
          <button
            type="button"
            className="remove-chip-btn"
            onClick={() => handleRemove(value)}
          >
            ×
          </button>
        )}
      </div>
    );
  };

  const defaultRenderAllChip = () => (
    <div className={`selected-chip all-chip ${chipClassName}`}>
      <span className="chip-text">All</span>
      {!disabled && (
        <button
          type="button"
          className="remove-chip-btn"
          onClick={handleToggleAll}
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <div className={`multi-select-container ${containerClassName}`}>
      {/* Select Dropdown */}
      <div className="multi-select-dropdown">
        <select
          className={selectClassName}
          onChange={handleSelectChange}
          value=""
          disabled={disabled}
          {...props}
        >
          <option value="" disabled>{placeholder}</option>
          {availableOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Selected Values Display - Only show when there are selected values */}
      {(currentValues.length > 0 || (allSelected && allSelectedValue)) && (
        <div className="selected-values-container">
          {allSelected && allSelectedValue ? (
            renderAllChip ? renderAllChip() : defaultRenderAllChip()
          ) : (
            <div className="selected-values-list">
              {currentValues.map((value, index) => 
                renderChip ? renderChip(value, index) : defaultRenderChip(value, index)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;